import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { FieldArray, Form, Formik } from 'formik';
import { onError } from '../../utils/error.util';
import { AffiliationFormSchema, affiliationFormInitialValues } from './validationSchema';
import './affiliation-form.css';

const emptyOwner = { name: '', ownershipPercentage: '', email: '', sameAsCompanyEmail: false, phone: '' };
const emptyStaff = { name: '', title: '', email: '', sameAsCompanyEmail: false };

export default function AffiliationFormContainer() {
  const { dealerId } = useParams<{ dealerId?: string }>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    document.body.classList.add('ccl-dash');
    return () => document.body.classList.remove('ccl-dash');
  }, []);

  const onSubmit = async (values: typeof affiliationFormInitialValues) => {
    setIsSubmitting(true);
    try {
      // TODO: wire up to API endpoint
      console.log('Affiliation form submission:', values);
      setSubmitted(true);
    } catch (err) {
      onError(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="ccl-dash">
        <section className="dealer-form-section py-5 text-center">
          <div className="container">
            <h2 className="white">Thank You!</h2>
            <p className="lead-text-light mt-3">
              Your affiliation application has been submitted. We will review it and be in touch shortly.
            </p>
          </div>
        </section>
      </div>
    );
  }

  return (
    <>
      {/* Header — overlays the hero */}
      <header className="ccl-affl-header">
        <section className="container">
          <div className="row">
            <div className="col-7 col-sm-5 col-md-5 col-lg-3 col-xl-2 py-2">
              <a href="https://www.collectorcarleaders.com" target="_blank" rel="noopener" title="Collector Car Leaders">
                <img alt="Collector Car Leaders" className="img-fluid" src="/assets/images/CCL-white.png" />
              </a>
            </div>
          </div>
        </section>
      </header>

      <main id="main">
        {/* Hero */}
        <section className="hero-section">
          <div className="container hero-content">
            <span className="hero-eyebrow">Collector Car Leaders</span>
            <h1>Affiliation</h1>
            <p className="hero-tagline">
              Welcome to the CCL family. Read through the affiliation packet below and submit your dealership details.
            </p>
          </div>
        </section>

        {/* To Get Started */}
        <section className="py-5 bg-light-ccl">
          <div className="container">
            <div className="row">
              <div className="col-12 text-center mb-5">
                <span className="section-eyebrow">Welcome</span>
                <h3 className="mt-2">To Get Started</h3>
                <div className="liner-center"></div>
                <p className="mt-3 mb-0 lead-text">
                  Please ensure that a member of the management team completes this packet.
                </p>
              </div>
              <div className="col-12 col-lg-10 mx-auto">
                <div className="info-card">
                  <h4>Affiliation Packet</h4>
                  <p>
                    Failure to include all required items may result in a delay in the affiliation process. Please
                    complete every section of this packet before submitting.
                  </p>
                </div>
                <div className="info-card">
                  <h4>Truepic Verification</h4>
                  <p>
                    You will receive an invitation via text message to your preferred mobile number, prompting you to
                    download the Truepic application. Once installed, Truepic will walk you through the process of
                    capturing the necessary photos to proceed.
                  </p>
                  <p>
                    After submitting the photos, a report will automatically be sent to CCL for review. It&rsquo;s
                    important to note that a Truepic will be required for each deal moving forward, so it may be useful
                    to keep the application downloaded.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Important Information */}
        <section className="py-5 bg-light-ccl">
          <div className="container">
            <div className="row">
              <div className="col-12 text-center mb-5">
                <span className="section-eyebrow">Before You Begin</span>
                <h3 className="mt-2">Important Information</h3>
                <div className="liner-center"></div>
              </div>
              <div className="col-11 col-lg-6 mx-auto mb-4">
                <div className="info-card">
                  <h4>Down Payment</h4>
                  <p>
                    The buyer will be instructed to send the down payment to you directly. Proof of down payment must
                    be provided for the first three deals funded before CCL will release funds.
                  </p>
                  <p className="info-card-list-label">Acceptable forms of down payment include:</p>
                  <ul className="info-card-list">
                    <li>Buyer&rsquo;s check</li>
                    <li>Wire transfer receipt</li>
                    <li>Credit card charge receipt or a photo of a personal check</li>
                  </ul>
                </div>
              </div>
              <div className="col-11 col-lg-6 mx-auto mb-4">
                <div className="info-card">
                  <h4>Insurance Policy Requirements</h4>
                  <p>
                    Your buyer will be required to provide an insurance policy binder or declaration page that meets
                    the requirements set by the lender. Please note that these requirements are determined solely by
                    the lender, and CCL does not have the authority to make any exceptions.
                  </p>
                  <p className="info-card-list-label">Lender insurance requirements include:</p>
                  <ul className="info-card-list">
                    <li>A guaranteed or agreed value insurance policy (not the same as a daily driver policy)</li>
                    <li>Deductibles can be no greater than $500</li>
                    <li>Lien holder must be listed exactly as provided to the buyer upon approval</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Title & Registration */}
        <section className="py-5 bg-light-ccl">
          <div className="container">
            <div className="row">
              <div className="col-12 text-center mb-5">
                <span className="section-eyebrow">Concierge Service</span>
                <h3 className="mt-2">Title &amp; Registration</h3>
                <div className="liner-center"></div>
              </div>
              <div className="col-12 col-lg-10 mx-auto">
                <div className="info-card">
                  <p>
                    At CCL, we pride ourselves on offering a concierge service experience, including comprehensive
                    title and registration processing tailored to meet the unique needs of each transaction. This
                    service may vary based on whether the dealer will handle sales tax collection and titling on behalf
                    of the buyer.
                  </p>
                  <div className="title-reg-scenario">
                    <h5>If the dealer collects sales tax and completes title transfer and registration for the buyer:</h5>
                    <p>
                      CCL&rsquo;s Title Specialist will provide the dealer representative with lien holder details and
                      ELT codes, if applicable. The Title Specialist will also request a copy of the Title Receipt or
                      Title Summary to confirm lien perfection.
                    </p>
                  </div>
                  <div className="title-reg-scenario">
                    <h5>If the dealer does not complete title transfer and registration for the buyer:</h5>
                    <p>
                      CCL includes sales tax, title, and registration fees in the financed amount. Our team will work
                      directly with the buyer, providing a detailed overview of the process and setting clear
                      expectations. We aim to manage as much of this process as possible, ensuring a seamless and
                      stress-free experience for the buyer.
                    </p>
                  </div>
                  <p>
                    For any additional questions or further clarification about our title and registration services,
                    please don&rsquo;t hesitate to reach out. A member of our title department will be happy to
                    assist you promptly.
                  </p>
                  <p>We look forward to collaborating with you to provide an exceptional experience for your customers.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── FORM ── */}
        <Formik
          initialValues={affiliationFormInitialValues}
          validationSchema={AffiliationFormSchema}
          onSubmit={onSubmit}
        >
          {({ handleChange, handleBlur, values, touched, errors }) => (
            <Form noValidate>
              <input type="hidden" name="dealer-id" id="dealer-id" value={dealerId ?? ''} />

              {/* ── Dealership Information ── */}
              <section className="bg-dk-blue white py-5 px-4 dealer-form-section" id="dealer-form">
                <div className="container">
                  <div className="row">
                    <div className="col-12 text-center mb-5">
                      <span className="section-eyebrow">Dealer Information</span>
                      <h2 className="white mt-2">Dealership Information</h2>
                      <div className="liner-center"></div>
                      <p className="lead-text-light">
                        Tell us about your dealership and the people who will be working with us.
                      </p>
                    </div>

                    <div className="col-12 col-lg-11 mx-auto">
                      <div className="sale-form-card">

                        {/* Business Information */}
                        <div className="form-section">
                          <h4 className="form-section-heading">Business Information</h4>
                          <div className="row">
                            <div className="col-md-6">
                              <label htmlFor="legalBusinessName">Legal Business Name</label>
                              <input
                                type="text"
                                id="legalBusinessName"
                                name="legalBusinessName"
                                className={`form-control${touched.legalBusinessName && errors.legalBusinessName ? ' is-invalid' : ''}`}
                                value={values.legalBusinessName}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                required
                              />
                              {touched.legalBusinessName && errors.legalBusinessName && (
                                <span className="field-error">{errors.legalBusinessName}</span>
                              )}
                            </div>
                            <div className="col-md-6">
                              <label htmlFor="dbaName">DBA <small style={{ textTransform: 'none', letterSpacing: 0 }}>(if applicable)</small></label>
                              <input
                                type="text"
                                id="dbaName"
                                name="dbaName"
                                className="form-control"
                                value={values.dbaName}
                                onChange={handleChange}
                                onBlur={handleBlur}
                              />
                            </div>
                            <div className="col-md-6">
                              <label htmlFor="ein">EIN <small style={{ textTransform: 'none', letterSpacing: 0 }}>(Employer Identification Number)</small></label>
                              <input
                                type="text"
                                id="ein"
                                name="ein"
                                className={`form-control${touched.ein && errors.ein ? ' is-invalid' : ''}`}
                                value={values.ein}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                required
                              />
                              {touched.ein && errors.ein && (
                                <span className="field-error">{errors.ein}</span>
                              )}
                            </div>
                            <div className="col-md-6">
                              <label>Length of Time in Business</label>
                              <div className="row g-2">
                                <div className="col-6">
                                  <input
                                    type="number"
                                    name="timeInBusinessYears"
                                    className={`form-control${touched.timeInBusinessYears && errors.timeInBusinessYears ? ' is-invalid' : ''}`}
                                    placeholder="Years"
                                    min="0"
                                    value={values.timeInBusinessYears}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    required
                                  />
                                  {touched.timeInBusinessYears && errors.timeInBusinessYears && (
                                    <span className="field-error">{errors.timeInBusinessYears as string}</span>
                                  )}
                                </div>
                                <div className="col-6">
                                  <input
                                    type="number"
                                    name="timeInBusinessMonths"
                                    className={`form-control${touched.timeInBusinessMonths && errors.timeInBusinessMonths ? ' is-invalid' : ''}`}
                                    placeholder="Months"
                                    min="0"
                                    max="11"
                                    value={values.timeInBusinessMonths}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    required
                                  />
                                  {touched.timeInBusinessMonths && errors.timeInBusinessMonths && (
                                    <span className="field-error">{errors.timeInBusinessMonths as string}</span>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="col-md-6">
                              <label htmlFor="businessPhone">Business Phone Number</label>
                              <input
                                type="text"
                                id="businessPhone"
                                name="businessPhone"
                                className={`form-control${touched.businessPhone && errors.businessPhone ? ' is-invalid' : ''}`}
                                value={values.businessPhone}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                required
                              />
                              {touched.businessPhone && errors.businessPhone && (
                                <span className="field-error">{errors.businessPhone}</span>
                              )}
                            </div>
                            <div className="col-md-6">
                              <label htmlFor="businessFax">Business Fax Number</label>
                              <input
                                type="text"
                                id="businessFax"
                                name="businessFax"
                                className="form-control"
                                value={values.businessFax}
                                onChange={handleChange}
                                onBlur={handleBlur}
                              />
                            </div>
                            <div className="col-md-6">
                              <label htmlFor="businessWebsite">Website</label>
                              <input
                                type="text"
                                id="businessWebsite"
                                name="businessWebsite"
                                className="form-control"
                                value={values.businessWebsite}
                                onChange={handleChange}
                                onBlur={handleBlur}
                              />
                            </div>
                            <div className="col-md-6">
                              <label htmlFor="companyEmail">Company-wide Email Address</label>
                              <input
                                type="email"
                                id="companyEmail"
                                name="companyEmail"
                                className={`form-control${touched.companyEmail && errors.companyEmail ? ' is-invalid' : ''}`}
                                value={values.companyEmail}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                required
                              />
                              <small className="form-help">
                                If there&rsquo;s an inbox that is received company-wide, please list it here.
                              </small>
                              {touched.companyEmail && errors.companyEmail && (
                                <span className="field-error">{errors.companyEmail}</span>
                              )}
                            </div>
                          </div>
                        </div>

                        <hr className="form-section-divider" />

                        {/* References */}
                        <div className="form-section">
                          <h4 className="form-section-heading">References</h4>
                          <div className="row">
                            <div className="col-md-6">
                              <label htmlFor="businessRef1">Business Reference 1</label>
                              <input
                                type="text"
                                id="businessRef1"
                                name="businessRef1"
                                className={`form-control${touched.businessRef1 && errors.businessRef1 ? ' is-invalid' : ''}`}
                                value={values.businessRef1}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                required
                              />
                              {touched.businessRef1 && errors.businessRef1 && (
                                <span className="field-error">{errors.businessRef1}</span>
                              )}
                            </div>
                            <div className="col-md-6">
                              <label htmlFor="businessRef1Phone">Phone</label>
                              <input
                                type="text"
                                id="businessRef1Phone"
                                name="businessRef1Phone"
                                className={`form-control${touched.businessRef1Phone && errors.businessRef1Phone ? ' is-invalid' : ''}`}
                                value={values.businessRef1Phone}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                required
                              />
                              {touched.businessRef1Phone && errors.businessRef1Phone && (
                                <span className="field-error">{errors.businessRef1Phone}</span>
                              )}
                            </div>
                            <div className="col-md-6">
                              <label htmlFor="businessRef2">Business Reference 2</label>
                              <input
                                type="text"
                                id="businessRef2"
                                name="businessRef2"
                                className={`form-control${touched.businessRef2 && errors.businessRef2 ? ' is-invalid' : ''}`}
                                value={values.businessRef2}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                required
                              />
                              {touched.businessRef2 && errors.businessRef2 && (
                                <span className="field-error">{errors.businessRef2}</span>
                              )}
                            </div>
                            <div className="col-md-6">
                              <label htmlFor="businessRef2Phone">Phone</label>
                              <input
                                type="text"
                                id="businessRef2Phone"
                                name="businessRef2Phone"
                                className={`form-control${touched.businessRef2Phone && errors.businessRef2Phone ? ' is-invalid' : ''}`}
                                value={values.businessRef2Phone}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                required
                              />
                              {touched.businessRef2Phone && errors.businessRef2Phone && (
                                <span className="field-error">{errors.businessRef2Phone}</span>
                              )}
                            </div>
                          </div>
                        </div>

                        <hr className="form-section-divider" />

                        {/* Physical Address */}
                        <div className="form-section">
                          <h4 className="form-section-heading">Physical Address</h4>
                          <div className="row">
                            <div className="col-12">
                              <label htmlFor="physicalAddress">Address</label>
                              <input
                                type="text"
                                id="physicalAddress"
                                name="physicalAddress"
                                className={`form-control${touched.physicalAddress && errors.physicalAddress ? ' is-invalid' : ''}`}
                                value={values.physicalAddress}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                required
                              />
                              {touched.physicalAddress && errors.physicalAddress && (
                                <span className="field-error">{errors.physicalAddress}</span>
                              )}
                            </div>
                            <div className="col-md-6">
                              <label htmlFor="physicalCity">City</label>
                              <input
                                type="text"
                                id="physicalCity"
                                name="physicalCity"
                                className={`form-control${touched.physicalCity && errors.physicalCity ? ' is-invalid' : ''}`}
                                value={values.physicalCity}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                required
                              />
                              {touched.physicalCity && errors.physicalCity && (
                                <span className="field-error">{errors.physicalCity}</span>
                              )}
                            </div>
                            <div className="col-6 col-md-3">
                              <label htmlFor="physicalState">State</label>
                              <input
                                type="text"
                                id="physicalState"
                                name="physicalState"
                                className={`form-control${touched.physicalState && errors.physicalState ? ' is-invalid' : ''}`}
                                value={values.physicalState}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                required
                              />
                              {touched.physicalState && errors.physicalState && (
                                <span className="field-error">{errors.physicalState}</span>
                              )}
                            </div>
                            <div className="col-6 col-md-3">
                              <label htmlFor="physicalZip">Zip</label>
                              <input
                                type="text"
                                id="physicalZip"
                                name="physicalZip"
                                className={`form-control${touched.physicalZip && errors.physicalZip ? ' is-invalid' : ''}`}
                                value={values.physicalZip}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                required
                              />
                              {touched.physicalZip && errors.physicalZip && (
                                <span className="field-error">{errors.physicalZip}</span>
                              )}
                            </div>

                            <div className="col-12">
                              <p className="form-p">Is this where your inventory is stored?</p>
                              <label className="form-check form-check-inline radio-pill">
                                <input
                                  className="form-check-input"
                                  type="radio"
                                  name="inventoryStored"
                                  value="yes"
                                  checked={values.inventoryStored === 'yes'}
                                  onChange={handleChange}
                                />
                                <span className="form-check-label">Yes</span>
                              </label>
                              <label className="form-check form-check-inline radio-pill">
                                <input
                                  className="form-check-input"
                                  type="radio"
                                  name="inventoryStored"
                                  value="no"
                                  checked={values.inventoryStored === 'no'}
                                  onChange={handleChange}
                                />
                                <span className="form-check-label">No</span>
                              </label>
                              {touched.inventoryStored && errors.inventoryStored && (
                                <span className="field-error d-block mt-1">{errors.inventoryStored}</span>
                              )}
                            </div>

                            {values.inventoryStored === 'no' && (
                              <div className="col-12 conditional-block">
                                <p className="conditional-block-label">If no, provide inventory storage address:</p>
                                <div className="row">
                                  <div className="col-12">
                                    <label htmlFor="inventoryAddress">Address</label>
                                    <input
                                      type="text"
                                      id="inventoryAddress"
                                      name="inventoryAddress"
                                      className={`form-control${touched.inventoryAddress && errors.inventoryAddress ? ' is-invalid' : ''}`}
                                      value={values.inventoryAddress}
                                      onChange={handleChange}
                                      onBlur={handleBlur}
                                    />
                                    {touched.inventoryAddress && errors.inventoryAddress && (
                                      <span className="field-error">{errors.inventoryAddress}</span>
                                    )}
                                  </div>
                                  <div className="col-md-6">
                                    <label htmlFor="inventoryCity">City</label>
                                    <input
                                      type="text"
                                      id="inventoryCity"
                                      name="inventoryCity"
                                      className="form-control"
                                      value={values.inventoryCity}
                                      onChange={handleChange}
                                      onBlur={handleBlur}
                                    />
                                  </div>
                                  <div className="col-6 col-md-3">
                                    <label htmlFor="inventoryState">State</label>
                                    <input
                                      type="text"
                                      id="inventoryState"
                                      name="inventoryState"
                                      className="form-control"
                                      value={values.inventoryState}
                                      onChange={handleChange}
                                      onBlur={handleBlur}
                                    />
                                  </div>
                                  <div className="col-6 col-md-3">
                                    <label htmlFor="inventoryZip">Zip</label>
                                    <input
                                      type="text"
                                      id="inventoryZip"
                                      name="inventoryZip"
                                      className="form-control"
                                      value={values.inventoryZip}
                                      onChange={handleChange}
                                      onBlur={handleBlur}
                                    />
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>

                        <hr className="form-section-divider" />

                        {/* Mailing Address */}
                        <div className="form-section">
                          <h4 className="form-section-heading">Mailing Address</h4>
                          <label className="form-check mailing-same-check">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              name="mailingSameAsPhysical"
                              checked={values.mailingSameAsPhysical}
                              onChange={handleChange}
                            />
                            <span className="form-check-label">Same as physical address</span>
                          </label>
                          {!values.mailingSameAsPhysical && (
                            <div className="row mailing-address-fields">
                              <div className="col-12">
                                <label htmlFor="mailingAddress">Address</label>
                                <input
                                  type="text"
                                  id="mailingAddress"
                                  name="mailingAddress"
                                  className={`form-control${touched.mailingAddress && errors.mailingAddress ? ' is-invalid' : ''}`}
                                  value={values.mailingAddress}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  required
                                />
                                {touched.mailingAddress && errors.mailingAddress && (
                                  <span className="field-error">{errors.mailingAddress}</span>
                                )}
                              </div>
                              <div className="col-md-6">
                                <label htmlFor="mailingCity">City</label>
                                <input
                                  type="text"
                                  id="mailingCity"
                                  name="mailingCity"
                                  className={`form-control${touched.mailingCity && errors.mailingCity ? ' is-invalid' : ''}`}
                                  value={values.mailingCity}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  required
                                />
                                {touched.mailingCity && errors.mailingCity && (
                                  <span className="field-error">{errors.mailingCity}</span>
                                )}
                              </div>
                              <div className="col-6 col-md-3">
                                <label htmlFor="mailingState">State</label>
                                <input
                                  type="text"
                                  id="mailingState"
                                  name="mailingState"
                                  className={`form-control${touched.mailingState && errors.mailingState ? ' is-invalid' : ''}`}
                                  value={values.mailingState}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  required
                                />
                                {touched.mailingState && errors.mailingState && (
                                  <span className="field-error">{errors.mailingState}</span>
                                )}
                              </div>
                              <div className="col-6 col-md-3">
                                <label htmlFor="mailingZip">Zip</label>
                                <input
                                  type="text"
                                  id="mailingZip"
                                  name="mailingZip"
                                  className={`form-control${touched.mailingZip && errors.mailingZip ? ' is-invalid' : ''}`}
                                  value={values.mailingZip}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  required
                                />
                                {touched.mailingZip && errors.mailingZip && (
                                  <span className="field-error">{errors.mailingZip}</span>
                                )}
                              </div>
                            </div>
                          )}
                        </div>

                        <hr className="form-section-divider" />

                        {/* Additional Sales Locations */}
                        <div className="form-section">
                          <h4 className="form-section-heading">Additional Sales Locations</h4>
                          <div className="row">
                            <div className="col-12">
                              <p className="form-p">Are there additional sales locations?</p>
                              <label className="form-check form-check-inline radio-pill">
                                <input
                                  className="form-check-input"
                                  type="radio"
                                  name="additionalSalesLocations"
                                  value="yes"
                                  checked={values.additionalSalesLocations === 'yes'}
                                  onChange={handleChange}
                                />
                                <span className="form-check-label">Yes</span>
                              </label>
                              <label className="form-check form-check-inline radio-pill">
                                <input
                                  className="form-check-input"
                                  type="radio"
                                  name="additionalSalesLocations"
                                  value="no"
                                  checked={values.additionalSalesLocations === 'no'}
                                  onChange={handleChange}
                                />
                                <span className="form-check-label">No</span>
                              </label>
                              {touched.additionalSalesLocations && errors.additionalSalesLocations && (
                                <span className="field-error d-block mt-1">{errors.additionalSalesLocations}</span>
                              )}
                            </div>

                            {values.additionalSalesLocations === 'yes' && (
                              <div className="col-12 conditional-block">
                                <p className="form-p" style={{ marginTop: 0 }}>Do additional locations fund to the same account?</p>
                                <label className="form-check form-check-inline radio-pill">
                                  <input
                                    className="form-check-input"
                                    type="radio"
                                    name="additionalLocationsSameAccount"
                                    value="yes"
                                    checked={values.additionalLocationsSameAccount === 'yes'}
                                    onChange={handleChange}
                                  />
                                  <span className="form-check-label">Yes</span>
                                </label>
                                <label className="form-check form-check-inline radio-pill">
                                  <input
                                    className="form-check-input"
                                    type="radio"
                                    name="additionalLocationsSameAccount"
                                    value="no"
                                    checked={values.additionalLocationsSameAccount === 'no'}
                                    onChange={handleChange}
                                  />
                                  <span className="form-check-label">No</span>
                                </label>

                                <hr className="form-section-divider" style={{ margin: '24px 0' }} />

                                <p className="conditional-block-label">Additional Location 1</p>
                                <div className="row">
                                  <div className="col-12">
                                    <label htmlFor="additionalLocation1Address">Address</label>
                                    <input
                                      type="text"
                                      id="additionalLocation1Address"
                                      name="additionalLocation1Address"
                                      className={`form-control${touched.additionalLocation1Address && errors.additionalLocation1Address ? ' is-invalid' : ''}`}
                                      value={values.additionalLocation1Address}
                                      onChange={handleChange}
                                      onBlur={handleBlur}
                                    />
                                    {touched.additionalLocation1Address && errors.additionalLocation1Address && (
                                      <span className="field-error">{errors.additionalLocation1Address}</span>
                                    )}
                                  </div>
                                  <div className="col-md-6">
                                    <label htmlFor="additionalLocation1City">City</label>
                                    <input type="text" id="additionalLocation1City" name="additionalLocation1City" className="form-control" value={values.additionalLocation1City} onChange={handleChange} onBlur={handleBlur} />
                                  </div>
                                  <div className="col-6 col-md-3">
                                    <label htmlFor="additionalLocation1State">State</label>
                                    <input type="text" id="additionalLocation1State" name="additionalLocation1State" className="form-control" value={values.additionalLocation1State} onChange={handleChange} onBlur={handleBlur} />
                                  </div>
                                  <div className="col-6 col-md-3">
                                    <label htmlFor="additionalLocation1Zip">Zip</label>
                                    <input type="text" id="additionalLocation1Zip" name="additionalLocation1Zip" className="form-control" value={values.additionalLocation1Zip} onChange={handleChange} onBlur={handleBlur} />
                                  </div>
                                </div>

                                <hr className="form-section-divider" style={{ margin: '24px 0' }} />

                                <p className="conditional-block-label">
                                  Additional Location 2{' '}
                                  <small style={{ fontWeight: 500, textTransform: 'none', letterSpacing: 0 }}>(if applicable)</small>
                                </p>
                                <div className="row">
                                  <div className="col-12">
                                    <label htmlFor="additionalLocation2Address">Address</label>
                                    <input type="text" id="additionalLocation2Address" name="additionalLocation2Address" className="form-control" value={values.additionalLocation2Address} onChange={handleChange} onBlur={handleBlur} />
                                  </div>
                                  <div className="col-md-6">
                                    <label htmlFor="additionalLocation2City">City</label>
                                    <input type="text" id="additionalLocation2City" name="additionalLocation2City" className="form-control" value={values.additionalLocation2City} onChange={handleChange} onBlur={handleBlur} />
                                  </div>
                                  <div className="col-6 col-md-3">
                                    <label htmlFor="additionalLocation2State">State</label>
                                    <input type="text" id="additionalLocation2State" name="additionalLocation2State" className="form-control" value={values.additionalLocation2State} onChange={handleChange} onBlur={handleBlur} />
                                  </div>
                                  <div className="col-6 col-md-3">
                                    <label htmlFor="additionalLocation2Zip">Zip</label>
                                    <input type="text" id="additionalLocation2Zip" name="additionalLocation2Zip" className="form-control" value={values.additionalLocation2Zip} onChange={handleChange} onBlur={handleBlur} />
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>

                        <hr className="form-section-divider" />

                        {/* Disclosures */}
                        <div className="form-section">
                          <h4 className="form-section-heading">Disclosures</h4>
                          <div className="row">

                            <div className="col-12">
                              <p className="form-p">Has the business, any parent company, subsidiary, or affiliated entity ever filed bankruptcy?</p>
                              <label className="form-check form-check-inline radio-pill">
                                <input className="form-check-input" type="radio" name="businessBankruptcy" value="yes" checked={values.businessBankruptcy === 'yes'} onChange={handleChange} />
                                <span className="form-check-label">Yes</span>
                              </label>
                              <label className="form-check form-check-inline radio-pill">
                                <input className="form-check-input" type="radio" name="businessBankruptcy" value="no" checked={values.businessBankruptcy === 'no'} onChange={handleChange} />
                                <span className="form-check-label">No</span>
                              </label>
                              {touched.businessBankruptcy && errors.businessBankruptcy && (
                                <span className="field-error d-block mt-1">{errors.businessBankruptcy}</span>
                              )}
                            </div>

                            {values.businessBankruptcy === 'yes' && (
                              <div className="col-12 conditional-block">
                                <label htmlFor="businessBankruptcyDetails">If yes, list business name, date of filing and explanation:</label>
                                <input
                                  type="text"
                                  id="businessBankruptcyDetails"
                                  name="businessBankruptcyDetails"
                                  className={`form-control${touched.businessBankruptcyDetails && errors.businessBankruptcyDetails ? ' is-invalid' : ''}`}
                                  value={values.businessBankruptcyDetails}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                />
                                {touched.businessBankruptcyDetails && errors.businessBankruptcyDetails && (
                                  <span className="field-error">{errors.businessBankruptcyDetails}</span>
                                )}
                              </div>
                            )}

                            <div className="col-12">
                              <p className="form-p">Has any owner, officer, partner, member or manager ever filed bankruptcy?</p>
                              <label className="form-check form-check-inline radio-pill">
                                <input className="form-check-input" type="radio" name="ownerBankruptcy" value="yes" checked={values.ownerBankruptcy === 'yes'} onChange={handleChange} />
                                <span className="form-check-label">Yes</span>
                              </label>
                              <label className="form-check form-check-inline radio-pill">
                                <input className="form-check-input" type="radio" name="ownerBankruptcy" value="no" checked={values.ownerBankruptcy === 'no'} onChange={handleChange} />
                                <span className="form-check-label">No</span>
                              </label>
                              {touched.ownerBankruptcy && errors.ownerBankruptcy && (
                                <span className="field-error d-block mt-1">{errors.ownerBankruptcy}</span>
                              )}
                            </div>

                            <div className="col-12">
                              <p className="form-p">Has any owner, officer, partner, member or manager ever been convicted of a felony?</p>
                              <label className="form-check form-check-inline radio-pill">
                                <input className="form-check-input" type="radio" name="ownerFelony" value="yes" checked={values.ownerFelony === 'yes'} onChange={handleChange} />
                                <span className="form-check-label">Yes</span>
                              </label>
                              <label className="form-check form-check-inline radio-pill">
                                <input className="form-check-input" type="radio" name="ownerFelony" value="no" checked={values.ownerFelony === 'no'} onChange={handleChange} />
                                <span className="form-check-label">No</span>
                              </label>
                              {touched.ownerFelony && errors.ownerFelony && (
                                <span className="field-error d-block mt-1">{errors.ownerFelony}</span>
                              )}
                            </div>

                            {values.ownerFelony === 'yes' && (
                              <div className="col-12 conditional-block">
                                <label htmlFor="ownerFelonyDetails">If yes, list name, offense, and date of conviction/circumstance:</label>
                                <input
                                  type="text"
                                  id="ownerFelonyDetails"
                                  name="ownerFelonyDetails"
                                  className={`form-control${touched.ownerFelonyDetails && errors.ownerFelonyDetails ? ' is-invalid' : ''}`}
                                  value={values.ownerFelonyDetails}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                />
                                {touched.ownerFelonyDetails && errors.ownerFelonyDetails && (
                                  <span className="field-error">{errors.ownerFelonyDetails}</span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>

                      </div>{/* sale-form-card */}
                    </div>
                  </div>
                </div>
              </section>

              {/* ── Staff Directory ── */}
              <section className="bg-dk-blue white py-5 px-4 dealer-form-section" id="staff-directory-form">
                <div className="container">
                  <div className="row">
                    <div className="col-12 text-center mb-5">
                      <span className="section-eyebrow">Your Team</span>
                      <h2 className="white mt-2">Staff Directory</h2>
                      <div className="liner-center"></div>
                      <p className="lead-text-light">List the people we&rsquo;ll be working with at every stage of the deal.</p>
                    </div>

                    <div className="col-12 col-lg-11 mx-auto">
                      <div className="sale-form-card">

                        {/* Owners */}
                        <div className="form-section">
                          <h4 className="form-section-heading">Owner(s)</h4>
                          <FieldArray name="owners">
                            {({ push, remove }) => (
                              <>
                                {values.owners.map((owner, i) => (
                                  <div key={i} className="row staff-block mb-4">
                                    {i > 0 && (
                                      <div className="col-12 btn-remove-block-wrapper">
                                        <button type="button" className="btn-remove-block" onClick={() => remove(i)}>
                                          Remove Owner {i + 1}
                                        </button>
                                      </div>
                                    )}
                                    <div className="col-sm-6">
                                      <label>Name</label>
                                      <input
                                        type="text"
                                        name={`owners[${i}].name`}
                                        className={`form-control${(touched.owners as any)?.[i]?.name && (errors.owners as any)?.[i]?.name ? ' is-invalid' : ''}`}
                                        value={owner.name}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                      />
                                      {(touched.owners as any)?.[i]?.name && (errors.owners as any)?.[i]?.name && (
                                        <span className="field-error">{(errors.owners as any)[i].name}</span>
                                      )}
                                    </div>
                                    <div className="col-sm-6">
                                      <label>Ownership %</label>
                                      <input
                                        type="number"
                                        name={`owners[${i}].ownershipPercentage`}
                                        className={`form-control${(touched.owners as any)?.[i]?.ownershipPercentage && (errors.owners as any)?.[i]?.ownershipPercentage ? ' is-invalid' : ''}`}
                                        value={owner.ownershipPercentage}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        min="1"
                                        max="100"
                                      />
                                      {(touched.owners as any)?.[i]?.ownershipPercentage && (errors.owners as any)?.[i]?.ownershipPercentage && (
                                        <span className="field-error">{(errors.owners as any)[i].ownershipPercentage}</span>
                                      )}
                                    </div>
                                    <div className="col-sm-6">
                                      <label>Phone</label>
                                      <input
                                        type="text"
                                        name={`owners[${i}].phone`}
                                        className={`form-control${(touched.owners as any)?.[i]?.phone && (errors.owners as any)?.[i]?.phone ? ' is-invalid' : ''}`}
                                        value={owner.phone}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                      />
                                      {(touched.owners as any)?.[i]?.phone && (errors.owners as any)?.[i]?.phone && (
                                        <span className="field-error">{(errors.owners as any)[i].phone}</span>
                                      )}
                                    </div>
                                    <div className="col-sm-6">
                                      <label>Email</label>
                                      <input
                                        type="email"
                                        name={`owners[${i}].email`}
                                        className={`form-control${(touched.owners as any)?.[i]?.email && (errors.owners as any)?.[i]?.email ? ' is-invalid' : ''}`}
                                        value={owner.email}
                                        disabled={owner.sameAsCompanyEmail}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                      />
                                      <label className="form-check same-email-check">
                                        <input
                                          className="form-check-input"
                                          type="checkbox"
                                          name={`owners[${i}].sameAsCompanyEmail`}
                                          checked={owner.sameAsCompanyEmail}
                                          onChange={handleChange}
                                        />
                                        <span className="form-check-label">Same as company-wide email</span>
                                      </label>
                                      {(touched.owners as any)?.[i]?.email && (errors.owners as any)?.[i]?.email && (
                                        <span className="field-error">{(errors.owners as any)[i].email}</span>
                                      )}
                                    </div>
                                  </div>
                                ))}
                                <button type="button" className="btn-add-block" onClick={() => push({ ...emptyOwner })}>
                                  + Add Owner
                                </button>
                              </>
                            )}
                          </FieldArray>
                        </div>

                        <hr className="form-section-divider" />

                        {/* Management */}
                        <StaffSection
                          label="Management"
                          fieldName="management"
                          values={values}
                          touched={touched}
                          errors={errors}
                          handleChange={handleChange}
                          handleBlur={handleBlur}
                          emptyItem={emptyStaff}
                        />

                        <hr className="form-section-divider" />

                        {/* Office & Titling */}
                        <StaffSection
                          label="Office & Titling"
                          fieldName="officeTitling"
                          values={values}
                          touched={touched}
                          errors={errors}
                          handleChange={handleChange}
                          handleBlur={handleBlur}
                          emptyItem={emptyStaff}
                        />

                        <hr className="form-section-divider" />

                        {/* Sales */}
                        <StaffSection
                          label="Sales"
                          fieldName="sales"
                          values={values}
                          touched={touched}
                          errors={errors}
                          handleChange={handleChange}
                          handleBlur={handleBlur}
                          emptyItem={emptyStaff}
                        />

                      </div>{/* sale-form-card */}
                    </div>
                  </div>
                </div>
              </section>

              {/* Submit */}
              <section className="bg-dk-blue dealer-form-section" style={{ borderTop: 'none', paddingTop: 0 }}>
                <div className="container pb-5">
                  <div className="row">
                    <div className="col-12 col-lg-11 mx-auto text-center">
                      <button id="btnSubmit" type="submit" disabled={isSubmitting}>
                        {isSubmitting ? 'Submitting...' : 'Submit Application'}
                      </button>
                    </div>
                  </div>
                </div>
              </section>

            </Form>
          )}
        </Formik>
      </main>

      {/* Footer */}
      <footer className="ccl-footer">
        <a href="https://www.collectorcarleaders.com" target="_blank" rel="noopener">
          <img src="/assets/images/CCL-white.png" alt="Collector Car Leaders" />
        </a>
        <p className="ccl-footer-url">
          <a href="https://www.collectorcarleaders.com" target="_blank" rel="noopener">
            collectorcarleaders.com
          </a>
        </p>
      </footer>
    </>
  );
}

interface StaffSectionProps {
  label: string;
  fieldName: string;
  values: any;
  touched: any;
  errors: any;
  handleChange: any;
  handleBlur: any;
  emptyItem: Record<string, any>;
}

function StaffSection({ label, fieldName, values, touched, errors, handleChange, handleBlur, emptyItem }: StaffSectionProps) {
  const items: any[] = values[fieldName] ?? [];

  return (
    <div className="form-section">
      <h4 className="form-section-heading">{label}</h4>
      <FieldArray name={fieldName}>
        {({ push, remove }) => (
          <>
            {items.map((item, i) => (
              <div key={i} className="row staff-block mb-4">
                <div className="col-12 btn-remove-block-wrapper">
                  <button type="button" className="btn-remove-block" onClick={() => remove(i)}>
                    Remove
                  </button>
                </div>
                <div className="col-sm-6">
                  <label>Name</label>
                  <input
                    type="text"
                    name={`${fieldName}[${i}].name`}
                    className={`form-control${touched[fieldName]?.[i]?.name && errors[fieldName]?.[i]?.name ? ' is-invalid' : ''}`}
                    value={item.name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  {touched[fieldName]?.[i]?.name && errors[fieldName]?.[i]?.name && (
                    <span className="field-error">{errors[fieldName][i].name}</span>
                  )}
                </div>
                <div className="col-sm-6">
                  <label>Title</label>
                  <input
                    type="text"
                    name={`${fieldName}[${i}].title`}
                    className={`form-control${touched[fieldName]?.[i]?.title && errors[fieldName]?.[i]?.title ? ' is-invalid' : ''}`}
                    value={item.title}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  {touched[fieldName]?.[i]?.title && errors[fieldName]?.[i]?.title && (
                    <span className="field-error">{errors[fieldName][i].title}</span>
                  )}
                </div>
                <div className="col-sm-6">
                  <label>Email</label>
                  <input
                    type="email"
                    name={`${fieldName}[${i}].email`}
                    className={`form-control${touched[fieldName]?.[i]?.email && errors[fieldName]?.[i]?.email ? ' is-invalid' : ''}`}
                    value={item.email}
                    disabled={item.sameAsCompanyEmail}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  <label className="form-check same-email-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      name={`${fieldName}[${i}].sameAsCompanyEmail`}
                      checked={item.sameAsCompanyEmail}
                      onChange={handleChange}
                    />
                    <span className="form-check-label">Same as company-wide email</span>
                  </label>
                  {touched[fieldName]?.[i]?.email && errors[fieldName]?.[i]?.email && (
                    <span className="field-error">{errors[fieldName][i].email}</span>
                  )}
                </div>
              </div>
            ))}
            <button type="button" className="btn-add-block" onClick={() => push({ ...emptyItem })}>
              + Add {label}
            </button>
          </>
        )}
      </FieldArray>
    </div>
  );
}
